import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkle, MagnifyingGlass, ArrowSquareOut } from "@phosphor-icons/react";

const EXAMPLES = ["Which clients received Erleada in the last 90 days?", "Open shipments to Brazil that are on hold", "Stock batches expiring before March 2027", "Unpaid invoices older than 30 days", "Quotes sent this month"];
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "yes" : "no") : String(v).length > 60 ? String(v).slice(0, 60) + "…" : String(v));

export const AskErp = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const run = async (question = q) => {
    if (!question.trim()) return;
    setQ(question); setBusy(true); setErr(""); setRes(null);
    try { const { data } = await api.post("/ask", { question }); setRes(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not answer"); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setOpen(true)} title="Ask the ERP" data-testid="ask-erp-button"><Sparkle size={16} weight="fill" className="text-[#F5B700] sm:mr-2" /><span className="hidden sm:inline">Ask</span></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto" data-testid="ask-erp-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-[#F5B700]" /> Ask the ERP</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask in plain language, e.g. which clients received Erleada in the last 90 days?" className="rounded-sm" data-testid="ask-erp-input" />
            <Button type="submit" className="rounded-sm shrink-0" disabled={busy || !q.trim()} data-testid="ask-erp-submit"><MagnifyingGlass size={16} className="mr-1.5" /> {busy ? "Thinking…" : "Ask"}</Button>
          </form>
          {!res && !busy && !err && (
            <div className="flex flex-wrap gap-2" data-testid="ask-erp-examples">
              {EXAMPLES.map((ex) => <button key={ex} type="button" onClick={() => run(ex)} className="text-xs rounded-full border border-border px-3 py-1 hover:bg-muted/60 text-left">{ex}</button>)}
            </div>
          )}
          {err && <p className="text-sm rounded-sm border border-red-200 bg-red-50 text-red-800 px-3 py-2" data-testid="ask-erp-error">{err}</p>}
          {res && (
            <div className="space-y-2" data-testid="ask-erp-result">
              <p className="text-sm"><span className="font-semibold">{res.total}</span> result{res.total === 1 ? "" : "s"} in <span className="capitalize">{res.collection}</span>{res.summary ? ` — ${res.summary}` : ""}{res.total > res.rows.length ? ` (showing first ${res.rows.length})` : ""}</p>
              {res.rows.length > 0 ? (
                <div className="border border-border rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
                      <tr>{res.columns.map((c) => <th key={c} className="px-3 py-2 font-semibold whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}<th className="px-3 py-2" /></tr>
                    </thead>
                    <tbody>
                      {res.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`ask-erp-row-${i}`}>
                          {res.columns.map((c) => <td key={c} className="px-3 py-2 align-top max-w-[280px] break-words">{fmt(r[c])}</td>)}
                          <td className="px-3 py-2 text-right">{r._link && !r._link.endsWith("/") && <button onClick={() => { setOpen(false); navigate(r._link); }} className="text-primary" title={`Open ${r._label}`} data-testid={`ask-erp-open-${i}`}><ArrowSquareOut size={16} /></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-sm text-muted-foreground">Nothing matched. Try rephrasing or widening the date range.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
